import { EventBus } from "../../contexts";
import { AuthContextValue, AuthUser } from "../../contexts/auth/types/auth";
import { KeycloakRole } from "../../contexts/auth/types/roles";
import { hasRealmRole, IDateValue, logger, LOGS } from "../../utils";
import { resizeImageToBase64 } from "../../utils/resizeImage/resizeImageToBase64";
import { ApiManager, TARGET_API_V2 } from "../api";
import { BannerManager } from "../banner";
import { ConsentIds, ConsentManager } from "../consentManager";
import { CustomViewManager } from "../customView/CustomViewManager";
import { DataManager } from "../dataManager/DataManager";
import { IaManager } from "../ia";
import { LicenseManager } from "../license";
import { LicenseFeatureFlag } from "../license/types/license.api";
import { MailManager } from "../mail";
import type { TodoItem } from "../mail/types/mail";
import { FeatureFlags } from "./FeatureFlags";
import { MailAnalysisFlag } from "./MailAnalysisFlag";
import { photoCache } from "./photoCache/photoCache";
import {
    AISuggestion,
    CONVERSATION_SUGGESTION,
    CUSTOMVIEW_CREATION_SUGGESTION,
    MAILVISTA_URLS,
} from "./types/const";
import { UserContext } from "./types/context";
import {
    DashboardWidgetSettings,
    EmailDisplayMode,
    Language,
    MailvistaUrls,
    Url,
} from "./types/settings";
import {
    FeatureFlag,
    UserProfile,
    UserSignature,
    UserType,
} from "./types/user";
import { UserContextLoader } from "./UserContextLoader";
import { UserProfileLoader } from "./UserProfileLoader";
import { UserTodos } from "./UserTodos";

/**
 * @class UserManager
 * ═══════════════════════════════════════════════════════════════════════════
 * Classe gérant les profils utilisateurs, leur contexte et leurs licences
 * Gère également la synchronisation avec la base de données et le DataManager (Google/Microsoft)
 */
export class UserManager {
    // #region Properties

    private authUser: AuthUser;

    private userId: string;

    private eventBus: EventBus;

    private authContext: AuthContextValue;

    private apiManager: ApiManager;

    private dataManager: DataManager;

    private mailManager: MailManager;

    private iaManager: IaManager;

    private bannerManager: BannerManager;

    private licenseManager: LicenseManager;

    private consentManager: ConsentManager;

    private customViewManager: CustomViewManager;

    private profileLoader: UserProfileLoader;

    private userTodos: UserTodos;

    private featureFlags: FeatureFlags;

    private apiTarget: TARGET_API_V2 = TARGET_API_V2.Api_Dev;

    private readonly uniqueId: number = Math.floor(Math.random() * 1000);

    private contextLoader: UserContextLoader;

    private mailAnalysisFlag: MailAnalysisFlag;

    // #endregion Properties

    //   /**
    //    * @constructor
    //    * Initialise un nouveau UserManager et charge le profil utilisateur initial
    //    * ═══════════════════════════════════════════════════════════════════════════
    //    * @input {AuthUser} authUser - Informations de l'utilisateur authentifié
    //    * @input {EventBus} eventBus - Bus d'événements pour la communication
    //    * @input {DataManager} dataManager - Gestionnaire Data API
    //    * @input {DbManager} dbManager - Gestionnaire de base de données
    //    * @input {IaManager} iaManager - Gestionnaire IA
    //    * @output {void}
    //    */
    constructor(
        authUser: AuthUser,
        eventBus: EventBus,
        authContext: AuthContextValue,
        apiTarget: TARGET_API_V2,
        profileLoader: UserProfileLoader,
        featureFlags: FeatureFlags,
        userTodos: UserTodos,
        contextLoader: UserContextLoader,
        mailAnalysisFlag: MailAnalysisFlag,
        dataManager: DataManager,
        iaManager: IaManager,
        licenseManager: LicenseManager,
        bannerManager: BannerManager,
        mailManager: MailManager,
        consentManager: ConsentManager,
        customViewManager: CustomViewManager,
        apiManager: ApiManager,
    ) {
        logger.info(LOGS.UserManager, `[${this.uniqueId}] - ()`);
        logger.info(
            [LOGS.UserManager],
            `[${this.uniqueId}] - UserManager - userId : ${authUser.id}`,
        );

        this.authUser = authUser;
        this.userId = authUser.id ?? "unknown";
        if (this.userId === "unknown") {
            logger.error(LOGS.UserManager, "No user id found in authUser !!!");
        }

        this.eventBus = eventBus;
        this.authContext = authContext;
        this.apiTarget = apiTarget;
        this.profileLoader = profileLoader;
        this.featureFlags = featureFlags;
        this.userTodos = userTodos;
        this.contextLoader = contextLoader;
        this.mailAnalysisFlag = mailAnalysisFlag;
        this.dataManager = dataManager;
        this.iaManager = iaManager;
        this.licenseManager = licenseManager;
        this.bannerManager = bannerManager;
        this.mailManager = mailManager;
        this.consentManager = consentManager;
        this.customViewManager = customViewManager;
        this.apiManager = apiManager;
    }

    // #region Initialisation and cleanup

    /**
     * Launch initial getUserProfile/syncUserProfile/createUserProfile/  -
     * Emits 'user-profile-updated' / 'user-created-profile-updated' -
     * Initialise Feature Flags
     */
    public async _initialize(): Promise<void> {
        const attempt = this.profileLoader.startBootstrapAttempt();
        logger.info(
            [LOGS.UserManager, LOGS.ServiceProvider],
            `UserManager._initialize() — tentative ${attempt}/${this.profileLoader.maxBootstrapAttempts}`,
        );

        logger.info(LOGS.UserProfile, "UserManager calls getUserProfile()");

        await this.getUserProfile(true);

        if (!this.profileLoader.cachedProfile) {
            logger.error(
                LOGS.UserManager,
                "UserProfileManager.initialize().getUserProfile(true) done but this.profileLoader.cachedProfile is null",
            );
        }

        await this.initialiseFeatureFlags();
    }

    /**
     * Phase post-consentement : charge le contexte utilisateur et la photo de profil.
     * Appelé par ServicesProvider en Phase 2 (après consentement Didomi),
     * garantissant que les données utilisateur dérivées de la messagerie
     * ne sont récupérées qu'après accord explicite.
     */
    public async _initializePostConsent(): Promise<void> {
        logger.info(
            [LOGS.UserManager, LOGS.ServiceProvider],
            "UserManager._initializePostConsent()",
        );

        // 1. Contexte utilisateur — nécessaire avant l'analyse des mails (Phase 3)
        logger.info([LOGS.UserManager], "  => Loading user context");
        await this.refreshUserContext();

        // 2. Démarrage du timer de refresh quotidien du contexte (seulement si licence valide)
        if (this.licenseManager.hasValidLicense()) {
            this.contextLoader.startRefreshTimer();
        }

        // 3. Photo de profil (côté client — non géré par le backend)
        if (this.profileLoader.cachedProfile) {
            const userPhoto = await this.getUserPhotoByEmail(
                this.profileLoader.cachedProfile.profile.email,
            );
            this.profileLoader.cachedProfile.photoURL = userPhoto;
            logger.info(
                LOGS.UserManager,
                "  User photo",
                this.profileLoader.cachedProfile.photoURL,
            );
            this.eventBus.emit(
                "user-profile-updated",
                this.profileLoader.cachedProfile,
            );
        }
    }

    /**
     * Launch initial getUserTodos() and emit 'startup-user-todos-updated'
     */
    public async _startUp(): Promise<void> {
        logger.info(
            [LOGS.UserManager, LOGS.ServiceProvider],
            "UserManager._startUp()",
        );

        const userTodos = await this.getUserTodos(true);

        this.eventBus.emit("startup-user-todos-updated", userTodos);

        const userDashboardTodos = userTodos.filter((todo) => !todo.done);
        this.eventBus.emit("user-todos-updated", userDashboardTodos);
    }

    // #endregion Initialisation and cleanup

    public async refreshUserContext(): Promise<UserContext> {
        return this.contextLoader.refreshUserContext();
    }

    public getUserContextHash(): string {
        return this.contextLoader.getUserContextHash();
    }

    public hasContextChanged(previousHash: string): boolean {
        return this.contextLoader.hasContextChanged(previousHash);
    }

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the UserManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.UserManager,
            "UserManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.UserManager,
                "  Phase 1: Stopping active processes",
            );

            // Cancel pending profile and todos fetch operations
            this.profileLoader._cleanup();
            this.userTodos._cleanup();
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `UserManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(LOGS.UserManager, "  Phase 2: Clearing internal state");

            // Clear feature flags array
            this.featureFlags.reset();

            // Reset userId to initial state (maintain consistency with constructor)
            this.userId = "unknown";

            // Reseting aPITarget
            this.apiTarget = TARGET_API_V2.Api_Dev;

            // Purge le cache photo via la façade (no-op sur web, Storage+TTL sur native)
            try {
                logger.info(LOGS.UserManager, "    Clearing photo cache");
                await photoCache.clear();
            } catch (cacheError) {
                logger.warn(
                    LOGS.UserManager,
                    `Error clearing photo cache: ${cacheError}`,
                );
            }
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `UserManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        // Phase 3: Release references
        try {
            logger.info(LOGS.UserManager, "  Phase 3: Releasing references");

            // User context cleanup
            this.contextLoader._cleanup();

            // Set authUser reference to undefined
            this.authUser = undefined as any;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `UserManager.cleanup() - Error in Phase 3: ${error}`,
            );
        }

        logger.info(
            LOGS.UserManager,
            "UserManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents() {
        this["user-todos-updated_handler"] =
            this["user-todos-updated_handler"].bind(this);
        this["user-consents-updated_handler"] =
            this["user-consents-updated_handler"].bind(this);
        this["custom-view-created_handler"] =
            this["custom-view-created_handler"].bind(this);
        this["custom-view-deleted_handler"] =
            this["custom-view-deleted_handler"].bind(this);
        this["mail-analysis-completed_handler"] =
            this["mail-analysis-completed_handler"].bind(this);
        this["max-ia-tokens-reached_handler"] =
            this["max-ia-tokens-reached_handler"].bind(this);

        this.eventBus.on(
            "user-todos-updated",
            this["user-todos-updated_handler"],
        );
        this.eventBus.on(
            "user-consents-updated",
            this["user-consents-updated_handler"],
        );
        this.eventBus.on(
            "custom-view-created",
            this["custom-view-created_handler"],
        );
        this.eventBus.on(
            "custom-view-deleted",
            this["custom-view-deleted_handler"],
        );
        this.eventBus.on(
            "mail-analysis-completed",
            this["mail-analysis-completed_handler"],
        );
        this.eventBus.on(
            "max-ia-tokens-reached",
            this["max-ia-tokens-reached_handler"],
        );
    }

    private async "user-todos-updated_handler"(
        userTodos: TodoItem[],
    ): Promise<void> {
        logger.info(
            [LOGS.UserProfile, LOGS.UserManager],
            "UserManager processing <user-todos-updated> event ...",
        );
        this.userTodos.replaceAll(userTodos);
    }

    private async "user-consents-updated_handler"(): Promise<void> {
        logger.info(
            [LOGS.ConsentManager, LOGS.UserManager],
            "UserManager processing <user-consents-updated> event ...",
        );

        await this.initialiseFeatureFlags();
    }

    private async "custom-view-created_handler"(): Promise<void> {
        logger.info(
            [LOGS.CustomViewManager, LOGS.UserManager],
            "UserManager processing <custom-view-created> event ...",
        );

        await this.initialiseFeatureFlags();
    }

    private async "custom-view-deleted_handler"(): Promise<void> {
        logger.info(
            [LOGS.CustomViewManager, LOGS.UserManager],
            "UserManager processing <custom-view-deleted> event ...",
        );

        await this.initialiseFeatureFlags();
    }

    private async "mail-analysis-completed_handler"(): Promise<void> {
        logger.info(
            LOGS.UserManager,
            `UserManager processing <mail-analysis-completed> event — recalculating MAIL_ENABLE_ANALYSIS flag ...`,
        );
        await this._setMailEnableAnalysis();
    }

    private async "max-ia-tokens-reached_handler"(): Promise<void> {
        logger.info(
            LOGS.UserManager,
            `UserManager processing <max-ia-tokens-reached> event — recalculating ASSISTANT_ENABLE_PROMPT flag ...`,
        );
        await this._setAssistantEnablePrompt();
        this.eventBus.emit("feature-flags-updated");
    }

    // #endregion Event Handlers

    // #region Public Methods

    // #region Profile

    public async getUserProfile(
        forceFetchNewProfile: boolean = false,
    ): Promise<UserProfile> {
        return this.profileLoader.getUserProfile(forceFetchNewProfile);
    }

    public cleanUserProfile(): void {
        this.profileLoader.cleanUserProfile();
    }

    /**
     * @method deleteUserProfile
     * Supprime le profil utilisateur dans la base de données
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     */
    public async deleteUserProfile(): Promise<boolean> {
        logger.info(LOGS.UserManager, "deleteUserProfile", this.userId);
        try {
            return true;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error deleting user profile: ${error}`,
            );
        }

        return false;
    }

    /**
     * Définit si l'onboarding de l'utilisateur est terminé
     * ═══════════════════════════════════════════════════════════════════════════
     * @param isOnboardingDone boolean
     */
    public async setIsOnboardingDone(isOnboardingDone: boolean) {
        logger.info(LOGS.UserManager, "setIsOnboardingDone()", this.userId);

        try {
            const updateUserResult = await this.apiManager.updateUserProfile({
                isOnboardingDone,
            });

            if (!updateUserResult) {
                logger.error(
                    LOGS.UserManager,
                    "updateUserResult is false, updateUserProfile Failed !!!",
                );
                throw new Error("Failed to update user profile");
            }

            if (this.profileLoader.cachedProfile) {
                this.profileLoader.cachedProfile.isOnboardingDone =
                    isOnboardingDone;
            }

            return updateUserResult;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error setIsOnboardingDone user : ${error}`,
            );
        }
    }

    // =========== Private methods ===========

    public _isUserJustCreated(): boolean {
        return this.profileLoader._isUserJustCreated();
    }

    // #endregion Profile

    // #region Todos

    public async getUserTodos(
        forceFetchTodosFromDb: boolean = false,
    ): Promise<TodoItem[]> {
        return this.userTodos.getUserTodos(forceFetchTodosFromDb);
    }

    public async getDashboardUserTodos(): Promise<TodoItem[]> {
        return this.userTodos.getDashboardUserTodos();
    }

    // #endregion Todos

    /**
     * @method getUserId
     * Retourne l'identifiant de l'utilisateur courant
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {string} Identifiant utilisateur
     * @throws {Error} Si aucun identifiant n'est trouvé
     */
    public getUserId(): string {
        if (this.userId === "unknown") {
            throw new Error("No user id found in User Manager !!!");
        }

        return this.userId;
    }

    /**
     * @method getLastLoginDate
     * Retourne la date de dernière connexion
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {string} Date de dernière connexion au format ISO
     */
    public getLastLoginDate(): IDateValue {
        return this.profileLoader.getLastLoginDate();
    }

    /**
     * Gets a user's profile photo by email address.
     *
     * Cascade : Api-Backend → favicon de domaine → initiales.
     *
     * La fraîcheur est gérée par la façade `photoCache` :
     *  - web    : no-op, le cache HTTP du navigateur (ETag) fait foi ;
     *  - native : Storage + TTL 24h (comportement historique préservé).
     */
    public async getUserPhotoByEmail(userEmail: string): Promise<string> {
        logger.info([LOGS.UserManager], `getUserPhotoByEmail(${userEmail})`);

        return photoCache
            .getOrLoad(`user_photo_${userEmail}`, () =>
                this.apiManager.getContactPhotoByEmail(userEmail),
            )
            .catch(() =>
                this.dataManager.generateInitialsPhoto(userEmail.split("@")[0]),
            );
    }

    // #region Suggestions IA

    /**
     * Get random AI suggestions based on the specified type
     * @param suggestionType - Type of suggestions to retrieve
     * @param count - Number of suggestions to return (default: 3)
     * @returns Array of suggestion strings
     */
    public getAISuggestions(
        suggestionType: AISuggestion,
        count: number = 3,
    ): string[] {
        logger.info(
            LOGS.UserManager,
            `getAISuggestions(${suggestionType}, ${count})`,
        );

        try {
            // Select the appropriate suggestion array based on type
            const suggestionArray =
                suggestionType === AISuggestion.Conversation
                    ? CONVERSATION_SUGGESTION
                    : CUSTOMVIEW_CREATION_SUGGESTION;

            // Create a copy of the array to avoid modifying the original
            const availableSuggestions = [...suggestionArray];
            const selectedSuggestions: string[] = [];

            // Ensure we don't request more suggestions than available
            const actualCount = Math.min(count, availableSuggestions.length);

            // Select random suggestions
            for (let i = 0; i < actualCount; i++) {
                // Generate random index
                const randomIndex = Math.floor(
                    Math.random() * availableSuggestions.length,
                );

                // Add to selected suggestions and remove from available options
                selectedSuggestions.push(availableSuggestions[randomIndex]);
                availableSuggestions.splice(randomIndex, 1);
            }

            return selectedSuggestions;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error generating AI suggestions: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);

            // Return empty array in case of error
            return [];
        }
    }

    // #endregion Suggestions IA

    // #region User Signatures Management

    /**
     * Récupère toutes les signatures pour l'utilisateur connecté
     * ═══════════════════════════════════════════════════════════════════════════
     * @returns Promise<UserSignature[]> - Liste des signatures utilisateur
     */
    public async getUserSignatures(): Promise<UserSignature[]> {
        logger.info(LOGS.UserManager, "getUserSignatures()");

        try {
            const result: UserSignature[] =
                await this.apiManager.getUserSignatures();

            logger.info(LOGS.UserManager, "User signatures retrieved", result);
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error retrieving user signatures: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Récupère une signature spécifique par son ID
     * ═══════════════════════════════════════════════════════════════════════════
     * @param signatureId - ID de la signature à récupérer
     * @returns Promise<UserSignature> - La signature demandée
     */
    public async getUserSignatureById(
        signatureId: string,
    ): Promise<UserSignature> {
        logger.info(LOGS.UserManager, `getUserSignatureById(${signatureId})`);

        try {
            const result: UserSignature =
                await this.apiManager.getUserSignatureById(signatureId);

            logger.info(LOGS.UserManager, "User signature retrieved", result);
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error retrieving user signature: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Crée une nouvelle signature utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @param userSignature - Données de la signature à créer (sans id, userId, isFavorite)
     * @returns Promise<UserSignature> - La signature créée
     */
    public async createUserSignature(
        userSignature: Omit<UserSignature, "id" | "userId" | "isFavorite">,
    ): Promise<UserSignature> {
        logger.info(
            LOGS.UserManager,
            "Creating signature with data",
            userSignature,
        );

        try {
            if (userSignature.avatar) {
                userSignature.avatar = await resizeImageToBase64(
                    userSignature.avatar,
                    200,
                );
            }

            const result: UserSignature =
                await this.apiManager.createUserSignature(userSignature);

            logger.info(LOGS.UserManager, "Created signature", result);
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error creating user signature: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Met à jour une signature utilisateur existante
     * ═══════════════════════════════════════════════════════════════════════════
     * @param userSignature - Données complètes de la signature à mettre à jour
     * @returns Promise<UserSignature> - La signature mise à jour
     */
    public async updateUserSignature(
        userSignature: UserSignature,
    ): Promise<UserSignature> {
        logger.info(
            LOGS.UserManager,
            `updateUserSignature(${userSignature.id})`,
        );
        logger.info(
            LOGS.UserManager,
            "Updating signature with data",
            userSignature,
        );

        try {
            if (userSignature.userId !== this.userId) {
                throw new Error(
                    `Signature ${userSignature.id} does not belong to current user ${this.userId}`,
                );
            }

            if (userSignature.avatar) {
                userSignature.avatar = await resizeImageToBase64(
                    userSignature.avatar,
                    200,
                );
            }

            const result: UserSignature =
                await this.apiManager.updateUserSignature(userSignature);

            logger.info(
                LOGS.UserManager,
                "User signature updated successfully",
            );
            logger.info(LOGS.UserManager, "Updated signature", result);
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error updating user signature: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Supprime une signature utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @param signatureId - ID de la signature à supprimer
     * @returns Promise<boolean> - True si la suppression a réussi
     */
    public async deleteUserSignature(signatureId: string): Promise<boolean> {
        logger.info(LOGS.UserManager, `deleteUserSignature(${signatureId})`);

        try {
            const result: boolean =
                await this.apiManager.deleteUserSignature(signatureId);

            if (result) {
                logger.info(
                    LOGS.UserManager,
                    "User signature deleted successfully",
                );
            }
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error deleting user signature: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    // #endregion User Signatures Management

    // #region Settings methods

    /**
     * Returns available languages
     * @returns {Language[]} Array containing existing Language values
     */
    public getLanguages(): Language[] {
        return Object.values(Language)
            .filter((value): value is Language => typeof value === "string")
            .map((value) => value as Language);
    }

    /**
     * Set the preferred language for the current user
     * @param preferredLanguage Language code to set as preferred
     * @returns Promise indicating success
     */
    public async setPreferredLanguage(
        preferredLanguage: string,
    ): Promise<boolean> {
        logger.info(LOGS.UserManager, "setPreferredLanguage()");

        try {
            // [AB#1834] migré Core (UserProfile_SetPreferredLanguage) → Api-Backend (PATCH v1/me/userProfile)
            const result: boolean = await this.apiManager.updateUserProfile({
                settings: { preferredLanguage: preferredLanguage as Language },
            });
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting preferred language: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Set the dashboard widgets configuration for the current user
     * @param dashboardWidgets Array of widget identifiers
     * @returns Promise indicating success
     */
    public async setDashboardWidgets(
        dashboardWidgets: DashboardWidgetSettings,
    ): Promise<boolean> {
        logger.info(LOGS.UserManager, "setDashboardWidgets()");

        try {
            // [AB#1834] migré Core (UserProfile_SetDashboardWidgets) → Api-Backend (PATCH v1/me/userProfile)
            const result: boolean = await this.apiManager.updateUserProfile({
                settings: { displayPreferences: { dashboardWidgets } },
            });
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting dashboard widgets: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Set the custom signature for the current user
     * @param customSignature Text to use as signature
     * @returns Promise indicating success
     */
    public async setCustomSignature(customSignature: string): Promise<boolean> {
        logger.info(LOGS.UserManager, "setCustomSignature()");

        try {
            // [AB#1834] migré Core (UserProfile_SetCustomSignature) → Api-Backend (PATCH v1/me/userProfile)
            // La chaîne vide est une valeur légitime (efface la signature).
            const result: boolean = await this.apiManager.updateUserProfile({
                settings: { customSignature },
            });
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting custom signature: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * Get the custom signature for the current user
     * @returns Promise with the user's custom signature text
     */
    public async getCustomSignature(): Promise<string> {
        logger.info(LOGS.UserManager, "getCustomSignature()");

        try {
            // [AB#1834] migré Core (UserProfile_GetCustomSignature) → Api-Backend (GET v1/me/userProfile)
            // Sémantique legacy conservée : erreur si profil introuvable, '' si signature absente.
            const userProfile = await this.apiManager.getUserProfile();
            if (!userProfile) {
                throw new Error("User profile not found");
            }

            const result: string = userProfile.settings?.customSignature ?? "";
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error retrieving custom signature: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    public getMailVistaUrls(): Record<MailvistaUrls, Url> {
        return MAILVISTA_URLS;
    }

    public async setUserEmailDisplayMode(
        emailDisplayMode: EmailDisplayMode,
    ): Promise<boolean> {
        logger.info(LOGS.UserManager, "setUserEmailDisplayMode()");

        try {
            // [AB#1834] migré Core (UserProfile_SetEmailDisplayMode) → Api-Backend (PATCH v1/me/userProfile)
            const result: boolean = await this.apiManager.updateUserProfile({
                settings: { displayPreferences: { emailDisplayMode } },
            });
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error setting User EmailDisplayMode: ${errorMessage}`;
            logger.error(LOGS.UserManager, errorText);
            throw new Error(errorText);
        }
    }

    // #endregion Language methods

    // #endregion Public Methods

    //#region Feature Flags

    /**
     * Check if featureFlag is enable for user
     */
    public isFeatureFlagEnabled(featureFlag: FeatureFlag): boolean {
        return this.featureFlags.isEnabled(featureFlag);
    }

    //#region Private Methods

    /**
     * Init all Feature Flags at start
     */
    private async initialiseFeatureFlags(): Promise<void> {
        // NOTE : Pendant sa période de test de 7 jours, l'utilisateur aura les mêmes accès que la version Premium.

        await Promise.all([
            this._setSettingsDisplayLicenseManagement(),
            this._setSettingsDisplayAdminDashboard(),
            this._setSettingsDisplayModelManagement(),
            this._setSettingsDisplaySignature(),
            this._setOnboardingDisplayWelcomeVideo(),
            this._setOnboardingDisplayNewFeature(),
            this._setMailEnableDeepFocus(),
            this._setMailEnableAnalysis(),
            this._setAssistantEnableVocal(),
            this._setAssistantEnablePrompt(),
            this._setAgendaEnableAccess(),
            this._setDeviceStorageEnableAccess(),
            this._setDashboardDisplayCo2(),
            this._setSystemEnableLogs(),
            this._setSupportEnableTicketCreation(),
            this._setCustomViewEnableCreation(),
        ]);

        this.eventBus.emit("feature-flags-updated");
    }

    private async _setSettingsDisplayLicenseManagement() {
        const flag = FeatureFlag.SETTINGS_DISPLAY_LICENSEMANAGEMENT;

        if (!this.profileLoader.cachedProfile) {
            this.featureFlags.set(flag, false);
            throw new Error(
                "Error during initialiseFeatureFlags - userProfileCache is not set",
            );
        }

        // Check if UserType in UserProfile is USER
        const isEnabled =
            this.profileLoader.cachedProfile.userType === UserType.USER;
        this.featureFlags.set(flag, isEnabled);
        return isEnabled;
    }

    private async _setSettingsDisplayAdminDashboard() {
        const flag = FeatureFlag.SETTINGS_DISPLAY_ADMINDASHBOARD;

        if (!this.profileLoader.cachedProfile) {
            this.featureFlags.set(flag, false);
            throw new Error(
                "Error during _setSettingsDisplayLicenseManagement - userProfileCache is not set",
            );
        }

        // Check if UserType in UserProfile is ENTERPRISE_MANAGER
        const isEnabled =
            this.profileLoader.cachedProfile.userType ===
            UserType.ENTERPRISE_MANAGER;
        this.featureFlags.set(flag, isEnabled);
        return isEnabled;
    }

    /**
     * SETTINGS_DISPLAY_MODELMANAGEMENT — accès à la page de sélection du modèle d'IA.
     * Dérive du rôle Keycloak `IA_ADMIN` porté par l'access_token (realm_access.roles).
     */
    private async _setSettingsDisplayModelManagement() {
        const flag = FeatureFlag.SETTINGS_DISPLAY_MODELMANAGEMENT;

        const isEnabled = await this._hasIaAdminRole();

        this.featureFlags.set(flag, isEnabled);
        return isEnabled;
    }

    /**
     * Indique si l'utilisateur porte le rôle `IA_ADMIN` dans son access_token Keycloak.
     * Le rôle est un rôle de realm → lu dans `realm_access.roles` (cf. `hasRealmRole`).
     */
    private async _hasIaAdminRole(): Promise<boolean> {
        try {
            const accessToken = await this.authContext.getAccessToken();

            return hasRealmRole(accessToken, KeycloakRole.IA_ADMIN);
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `_hasIaAdminRole - failed to read roles from token: ${error}`,
            );
            return false;
        }
    }

    private async _setSettingsDisplaySignature() {
        const flag = FeatureFlag.SETTINGS_DISPLAY_SIGNATURE;

        // check if userType = USER
        // OR
        // userType = ENT. USER/MANAGER + récup externalSignatureManagement=false in License TeamData with dbManager._getTeamLicenseData(teamId)
        if (!this.profileLoader.cachedProfile) {
            this.featureFlags.set(flag, false);
            throw new Error(
                "Error during _setSettingsDisplaySignature - userProfileCache is not set",
            );
        }

        try {
            const userType = this.profileLoader.cachedProfile.userType;

            // USER type always has signature management
            if (userType === UserType.USER) {
                this.featureFlags.set(flag, true);
                return true;
            }

            // For enterprise users, check externalSignatureManagement
            if (
                userType === UserType.ENTERPRISE_USER ||
                userType === UserType.ENTERPRISE_MANAGER
            ) {
                if (!this.profileLoader.cachedProfile.teamId) {
                    this.featureFlags.remove(flag);
                    throw new Error("teamId is not set");
                }

                const teamData = await this.apiManager.getOrganization(
                    this.profileLoader.cachedProfile.teamId,
                );
                const isEnabled = !teamData?.externalSignatureManagement;

                this.featureFlags.set(flag, isEnabled);
                return isEnabled;
            }
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking external signature management: ${error}`,
            );
            this.featureFlags.remove(flag);
            return false;
        }
    }

    private async _setOnboardingDisplayWelcomeVideo() {
        const flag = FeatureFlag.ONBOARDING_DISPLAY_WELCOMEVIDEO;

        if (!this.profileLoader.cachedProfile) {
            this.featureFlags.set(flag, false);
            throw new Error(
                "Error during _setOnboardingDisplayWelcomeVideo -  userProfileCache is not set",
            );
        }

        // Check if isOnboardingDone in userProfile is true/false
        const isEnabled = !this.profileLoader.cachedProfile.isOnboardingDone;
        this.featureFlags.set(flag, isEnabled);
        return isEnabled;
    }

    private async _setOnboardingDisplayNewFeature() {
        logger.warn(
            LOGS.UserManager,
            `set FeatureFlag : OnboardingDisplayNewFeature !!! Not implemented yet !!!`,
        );

        const flag = FeatureFlag.ONBOARDING_DISPLAY_NEWFEATURE;

        if (!this.profileLoader.cachedProfile) {
            this.featureFlags.set(flag, false);
            throw new Error(
                "Error during _setOnboardingDisplayNewFeature -  userProfileCache is not set",
            );
        }

        this.featureFlags.set(flag, true);
        return true;
    }

    private async _setMailEnableDeepFocus() {
        const flag = FeatureFlag.MAIL_ENABLE_DEEPFOCUS;

        try {
            const fullLicensesDetails =
                await this.licenseManager.getFullLicensesDetails();
            const hasValidLicense = this.licenseManager.hasValidLicense();
            const hasDeepFocusFlag = fullLicensesDetails.active.some((l) =>
                l.detail.featureFlags.includes(
                    LicenseFeatureFlag.MAIL_ENABLE_DEEPFOCUS,
                ),
            );

            const isEnabled = hasValidLicense && hasDeepFocusFlag;
            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking deep focus license: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    // Son seul déclencheur runtime est en aval de lui-même : sans rappel explicite, le flag reste figé.
    public async refreshMailAnalysisFlag(): Promise<void> {
        await this._setMailEnableAnalysis();
    }

    private _setMailEnableAnalysis() {
        return this.mailAnalysisFlag.refresh(() =>
            this.mailManager.getCurrentMonthEmailCount(),
        );
    }

    private async _setAssistantEnableVocal() {
        const flag = FeatureFlag.ASSISTANT_ENABLE_VOCAL;

        // Check if user get a license Ultimate or Premium
        // Check if userConsent = true
        try {
            const hasValidLicense = this.licenseManager.hasValidLicense();
            const isConsentGiven = await this.consentManager.isConsentGiven(
                ConsentIds.ACCESS_VOICE,
            );

            const isEnabled = hasValidLicense && isConsentGiven;

            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking license or consent for vocal assistant: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    private async _setAssistantEnablePrompt() {
        const flag = FeatureFlag.ASSISTANT_ENABLE_PROMPT;

        if (!this.profileLoader.cachedProfile) {
            this.featureFlags.set(flag, false);
            throw new Error("userProfileCache not found");
        }

        // Check if userlicense is active
        // Check if not all tokens consumed (real-time via iaManager)
        try {
            const hasValidLicense = this.licenseManager.hasValidLicense();

            // iaManager.getTokenStatus() est toujours à jour via 'ia-token-spent'
            // total === 0 signifie que le statut n'est pas encore chargé → on ne bloque pas
            const tokenStatus = this.iaManager.getTokenStatus();
            const hasTokens =
                !tokenStatus ||
                tokenStatus.total === 0 ||
                tokenStatus.used < tokenStatus.total;

            const isEnabled = hasValidLicense && hasTokens;
            logger.info(
                LOGS.UserManager,
                `_setAssistantEnablePrompt: valid=${hasValidLicense}, tokens=${tokenStatus?.used ?? "?"}/${tokenStatus?.total ?? "?"}, enabled=${isEnabled}`,
            );
            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking tokens for assistant prompt: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    private async _setAgendaEnableAccess() {
        logger.warn(
            LOGS.UserManager,
            `Set FeatureFlag : AgendaEnableAccess !!! Not implemented yet !!!`,
        );
        // Check in DataProvider if calendar available in provider of connexion

        const flag = FeatureFlag.AGENDA_ENABLE_ACCESS;
        this.featureFlags.set(flag, true);
        return true;
    }

    private async _setDeviceStorageEnableAccess() {
        const flag = FeatureFlag.DEVICESTORAGE_ENABLE_ACCESS;

        // Check if userConsent = true
        try {
            const isEnabled = await this.consentManager.isConsentGiven(
                ConsentIds.ACCESS_LOCAL_DATA,
            );
            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking consent enable access device storage: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    private async _setDashboardDisplayCo2() {
        // Check if userlicense is active OR Check if in grace delay

        const flag = FeatureFlag.DASHBOARD_DISPLAY_CO2;

        try {
            // TODO - Check grace delay
            logger.warn(
                LOGS.UserManager,
                `Grace delay not implemented yet !!!`,
            );
            const isEnabled = this.licenseManager.hasValidLicense();
            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking license for CO2 dashboard: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    private async _setSystemEnableLogs() {
        const flag = FeatureFlag.SYSTEM_ENABLE_LOGS;

        let enableLogFlags = false;

        if (this.apiTarget == TARGET_API_V2.Api_Dev) {
            enableLogFlags = true;
        }

        const email = this.profileLoader.cachedProfile?.profile.email?.trim();
        if (this.apiTarget == TARGET_API_V2.Api_Prod && email) {
            const productionLogWhiteList: string[] = [
                "julien.magne@teamway.fr",
                "alexandre.vedrine@teamway.fr",
                "martial.baur@teamway.fr",
            ];

            enableLogFlags = productionLogWhiteList.includes(
                email.toLowerCase(),
            );
        }

        this.featureFlags.set(flag, enableLogFlags);
        return false;
    }

    private async _setSupportEnableTicketCreation() {
        const flag = FeatureFlag.SUPPORT_ENABLE_TICKETCREATION;

        // Check if valid license ongoing
        try {
            // This ensure licenses are ready
            await this.licenseManager.getFullLicensesDetails();

            // Check if any license is still valid
            const isEnabled = this.licenseManager.hasValidLicense();

            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `Error checking license for support tickets: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    public async _setCustomViewEnableCreation(): Promise<boolean> {
        const flag = FeatureFlag.CUSTOMVIEW_ENABLE_CREATION;

        try {
            if (!this.licenseManager.hasValidLicense()) {
                logger.info(LOGS.UserManager, "Aucune licence active trouvée");
                this.featureFlags.set(flag, false);
                return false;
            }

            const [fullLicensesDetails, numberOfCustomViewCreated] =
                await Promise.all([
                    this.licenseManager.getFullLicensesDetails(),
                    this.customViewManager._getCustomViewsCount(),
                ]);

            const limitCustomViewCreation = Math.max(
                0,
                ...fullLicensesDetails.active.map(
                    (l) => l.detail.customViewLimit,
                ),
            );

            logger.info(
                [LOGS.UserManager],
                ` Limite de création de CustomView : ${limitCustomViewCreation}`,
            );
            logger.info(
                [LOGS.UserManager],
                ` Nombre de CustomView créées par user : ${numberOfCustomViewCreated}`,
            );

            const isEnabled: boolean =
                numberOfCustomViewCreated < limitCustomViewCreation;
            this.featureFlags.set(flag, isEnabled);
            return isEnabled;
        } catch (error) {
            logger.error(
                [LOGS.UserManager],
                `Error setting feature flag ${flag}: ${error}`,
            );
            this.featureFlags.set(flag, false);
            return false;
        }
    }

    //#endregion Private Methods
    //#endregion Feature Flags
}
